/* ============================================================
   permissions.js — the 104-tag vocabulary, mirrored from the backend.

   IMPORTANT — WHAT THIS DOES AND DOES NOT DO
   ------------------------------------------
   Everything here is COSMETIC: it hides nav items and blocks routes in the UI.
   Real enforcement is `require_permission` on the server, and today that only
   guards /users/*, /roles/* and /permissions/*. The other 20 screens are seed
   data with no server behind them. A ticked box in the RBAC matrix is not an access control.

   Source of truth: backend/users/permissions.py PermissionTag, which asserts at
   startup that the enum equals the full modules x actions cross-product.
   ============================================================ */

export const MODULES = [
  'dashboard', 'inbox', 'jobs', 'candidates', 'pipeline', 'interviews', 'assessments',
  'offers', 'reports', 'analytics', 'job_board', 'settings', 'rbac_users', 'tasks',
  'talent', 'requisitions', 'department', 'role_preview', 'activity',
  'cv_bank', 'job_matching', 'cv_import', 'progress', 'hiring_managers', 'recruiter_hub', 'calendar',
]

export const ACTIONS = [
  'view', 'create', 'edit', 'delete', 'approve', 'export', 'manage', 'configure',
]

/** All `module.action` tags (modules x actions cross-product). */
export const ALL_TAGS = MODULES.flatMap((m) => ACTIONS.map((a) => `${m}.${a}`))

/**
 * Build a permission predicate. A null/undefined tag is always allowed — routes
 * like Tasks and Help have no backend module and are open to any signed-in user.
 * An array means any one of those tags (Approvals: requisitions or offers).
 */
export function makeCan(permissions) {
  const set = new Set(permissions ?? [])
  return (tag) => !tag || (Array.isArray(tag) ? tag.some((t) => set.has(t)) : set.has(tag))
}

/* Screens, nav and actions key off `permissions` from /users/me only — never
   off role_name — so Access Control is the single switch for what a role sees.
   The one name check left is below, and it only mirrors the backend's ROW
   scoping (which records come back, and from which endpoint), not what the UI
   shows. */

/** Mirrors backend `is_hiring_manager`. Data routing only — never gate UI on it. */
function hasManagerRowScope(user) {
  const name = (user?.role_name || '').trim().toLowerCase()
  return name === 'hiring_manager' || name === 'manager'
}

/** Off: hiring managers use the /candidate/fetch list, scoped server-side
    to their jobs (job/candidate/views.py owned_job_ids_for_candidate_scope). */
export function usesManagerCandidateFeed() {
  return false
}

const ADMIN_ROLES = new Set(['system_administrator', 'hr_administrator', 'admin'])

/** View as role / user is a System Administrator feature (server-enforced).
 *  Pass the real role while a preview is active (preview.real_role_name). */
export function isSystemAdministrator(roleName) {
  return String(roleName || '').trim().toLowerCase().replace(/[\s-]+/g, '_') === 'system_administrator'
}

/* Offers → Place Offer (compensation proposal sheet). Recruiter Manager is the
   hiring_manager role key. Department Head / Department Manager are left out on
   purpose, as is every other role. Previewing a role answers for that role. */
const PLACE_OFFER_ROLES = new Set([
  'system_administrator', 'hr_administrator', 'recruiter', 'hiring_manager', 'recruiter_manager', 'recruitment_head',
])

export function canPlaceOffer(user) {
  const key = String(user?.role_name || '').trim().toLowerCase().replace(/[\s-]+/g, '_')
  return PLACE_OFFER_ROLES.has(key)
}

/** Department Managers and Heads (not admins) see only the departments they head or
    manage — mirrors backend users.permissions.own_department_ids. */
export function ownDepartmentsOnly(user) {
  const key = (user?.role_name || '').trim().toLowerCase().split(/\s+/).join('_')
  return !isAdmin(user) && (key === 'department_manager' || key === 'department_head')
}

export function isAdmin(user) {
  const name = (user?.role_name || '').trim().toLowerCase()
  return ADMIN_ROLES.has(name)
}

/** Unscoped Candidates list — matches backend sees_all_candidates. */
export function seesAllCandidates(user) {
  if (isAdmin(user)) return true
  return (user?.permissions || []).includes('candidates.manage')
}

/** Unscoped hiring-manager directory — Access Control Manage on that module. */
export function seesAllHiringManagers(user) {
  return (user?.permissions || []).includes('hiring_managers.manage')
}

/** Matches backend `scopes_module_to_department`. Tick <module> → Configure
    on any module (Departments, Jobs, Candidates, Hiring Managers, …).
    <module> → Manage unscopes. Independent of View and of isAdmin. */
export function scopesModuleToDepartment(user, module) {
  const granted = user?.permissions || []
  if (granted.includes(`${module}.manage`)) return false
  return granted.includes(`${module}.configure`)
}

/** Hiring-manager directory limited to jobs this user owns (ids_for_manager). */
export function scopesHiringManagersToDepartment(user) {
  return scopesModuleToDepartment(user, 'hiring_managers')
}

/** Jobs/candidates limited to requisitions this user created (or is assigned).

    Matches backend `scopes_to_own_requisitions`. Custom roles opt in from
    Access Control by ticking Requisitions → Configure (`requisitions.configure`),
    not Create. `requisitions.manage` unscopes the requisition list only. */
export function scopesToOwnRequisitions(user) {
  if (hasManagerRowScope(user)) return true
  if (isAdmin(user) || seesAllCandidates(user)) return false
  return (user?.permissions || []).includes('requisitions.configure')
}
